import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import "./ConfirmDialog.css";

const ConfirmContext = createContext(null);

const ICONS = {
  info: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
  warning: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  danger: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  ),
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolverRef = useRef(null);
  const confirmBtnRef = useRef(null);

  const confirm = useCallback((options) => {
    const variant = options?.variant || (options?.danger ? "danger" : "warning");
    setState({
      title: options?.title || "Please Confirm",
      message: options?.message || "Are you sure?",
      confirmLabel: options?.confirmLabel || "Confirm",
      cancelLabel: options?.cancelLabel || "Cancel",
      variant,
    });
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const close = useCallback((result) => {
    if (resolverRef.current) resolverRef.current(result);
    resolverRef.current = null;
    setState(null);
  }, []);

  useEffect(() => {
    if (!state) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") close(false);
      if (e.key === "Enter") close(true);
    };
    window.addEventListener("keydown", onKeyDown);
    confirmBtnRef.current?.focus();
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [state, close]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}

      {state && (
        <div className="cd-overlay" onClick={() => close(false)}>
          <div
            className="cd-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="cd-title"
            aria-describedby="cd-message"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`cd-accent cd-accent--${state.variant}`} />

            <div className={`cd-icon cd-icon--${state.variant}`}>
              {ICONS[state.variant]}
            </div>

            <h2 id="cd-title" className="cd-title">{state.title}</h2>
            <p id="cd-message" className="cd-message">{state.message}</p>

            <div className="cd-actions">
              <button type="button" className="cd-btn cd-btn--outline" onClick={() => close(false)}>
                {state.cancelLabel}
              </button>
              <button
                type="button"
                ref={confirmBtnRef}
                className={`cd-btn cd-btn--${state.variant}`}
                onClick={() => close(true)}
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used inside a <ConfirmProvider>");
  }
  return ctx;
}